import { EmptyState, LoadingSpinner } from "@/components/common";
import { Button, Input, ScrollArea } from "@/components/ui";
import type { Category, Product } from "@/types";
import { Coffee, PanelLeftClose, PanelLeft, Search } from "lucide-react";
import { CategoryChip } from "./CategoryChip";
import { ProductCard } from "./ProductCard";

interface ProductGridProps {
  search: string;
  onSearchChange: (value: string) => void;
  categories: Category[] | undefined;
  selectedCategoryId: string | null;
  onCategoryChange: (id: string | null) => void;
  products: Product[];
  isLoading: boolean;
  currencySymbol: string;
  onProductClick: (product: Product) => void;
  isExpanded: boolean;
  onToggleExpanded: () => void;
}

export function ProductGrid({
  search,
  onSearchChange,
  categories,
  selectedCategoryId,
  onCategoryChange,
  products,
  isLoading,
  currencySymbol,
  onProductClick,
  isExpanded,
  onToggleExpanded,
}: ProductGridProps) {
  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div className="px-4 pt-4 md:px-6 md:pt-6">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search menu..."
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              className="h-10 rounded-full border-none bg-card pl-11 text-base shadow-sm md:h-12"
              aria-label="Search menu"
            />
          </div>
          <Button
            variant="outline"
            size="icon"
            onClick={onToggleExpanded}
            className="hidden h-10 w-10 shrink-0 rounded-full md:flex md:h-12 md:w-12"
            aria-label={isExpanded ? "Show sidebar" : "Hide sidebar"}
          >
            {isExpanded ? (
              <PanelLeft className="h-4 w-4" />
            ) : (
              <PanelLeftClose className="h-4 w-4" />
            )}
          </Button>
        </div>
        <div className="mt-3 flex flex-wrap gap-2 pb-2 md:mt-4">
          <CategoryChip
            label="All"
            active={selectedCategoryId === null}
            onClick={() => onCategoryChange(null)}
          />
          {categories?.map((category) => (
            <CategoryChip
              key={category.id}
              label={category.name}
              active={selectedCategoryId === category.id}
              onClick={() => onCategoryChange(category.id)}
            />
          ))}
        </div>
      </div>
      <ScrollArea className="flex-1 p-4 md:p-6">
        {isLoading ? (
          <div className="flex h-full items-center justify-center">
            <LoadingSpinner text="Loading products..." />
          </div>
        ) : products.length === 0 ? (
          <EmptyState
            icon={Coffee}
            title="No products found"
            description={
              search ? "Try adjusting your search" : "No products available"
            }
          />
        ) : (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3 md:grid-cols-4 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
            {products.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                onClick={() => onProductClick(product)}
                currencySymbol={currencySymbol}
              />
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}
